import {Component} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DatePipe} from '@angular/common';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DataManagerService} from '../../../data/services/DataManager/data-manager.service';
import {AppConstants} from '../../../shared/constants/app-constants';
import {PermissionConstants} from '../../../shared/constants/permission-constants';
import {PaymentModel} from '../../../data/model/PaymentModel/payment.model';
import {DetailRow} from '../../../ui/detail-card/payarc-detail-card.component';
import {GetPaymentDetailsAction} from '../payment-details/get-payment-details.action';

/**
 * ACH-only counterpart to PaymentDetailsComponent (which still handles `?category=ach` for
 * two other modules' existing links — see PENDING-FINDINGS). The ACH list now routes here
 * directly instead of the shared card/ACH detail page.
 */
@Component({
    selector: 'app-payment-ach-details',
    templateUrl: './payment-ach-details.component.html',
    styleUrls: ['./payment-ach-details.component.css'],
    standalone: false
})
export class PaymentAchDetailsComponent extends GetPaymentDetailsAction {
  serviceNameACH = '/achcharges';
  selectedChargeId: string;
  paymentObj: PaymentModel = new PaymentModel();
  payment2 = {};
  editDescriptionFlag: boolean;
  defaultDescription: string;
  transaction_metadata: any;
  extra_metadata: any;
  returns_include: { data?: { return_code?: string, [key: string]: any }[] };
  returnsSectionTitle = 'Rejection details';
  eventListUrl: string;
  can_refund: boolean;
  redirect_link_customer = '/customer/customer-details';
  topErrorMessage: string;
  keys1: string[];
  errorMsg: string;
  private fileURL: string;

  constructor(router: Router, dataManager: DataManagerService, private _route: ActivatedRoute,
              private datePipe: DatePipe, snackBar: MatSnackBar) {
    super();
    this.snackBar = snackBar;
    this.router = router;
    this.route = _route;
    this.dataManager = dataManager;
    // Refund is hidden until the backend exposes a dedicated ACH refund endpoint — the shared
    // refund flow this would lead to only ever calls the card /charges/{id}/refunds endpoint,
    // which doesn't apply to an ACH charge id. See PENDING-FINDINGS.
    this.can_refund = false;
    this.update_permission = this.permissionCheck(PermissionConstants.PERMISSION_UPDATE_PAYMENT);
    this.redirect_link = '/payment/payment-ach-list';
    this._route.queryParams.subscribe((params) => {
      if (params['id']) {
        this.selectedChargeId = params['id'];
        this._getPaymentDetailsUrl = AppConstants.BASE_URL + this.serviceNameACH + '/' + this.selectedChargeId
          + '?include=review,transaction_metadata,extra_metadata,returns';
        this._updatePaymentDescriptionUrl = AppConstants.BASE_URL + this.serviceNameACH + '/' + this.selectedChargeId;
        this.eventListUrl = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_EVENT + '?related_id=' + this.selectedChargeId + '&type=charge';
        this.getPayment();
      }
    });
  }

  getPayment() {
    this.loadFlag = true;
    this.getPaymentDetails();
  }

  statusClass(status: string): string {
    if (status === 'settled') { return 'good'; }
    if (status === 'partial_refund' || status === 'pended_by_kount') { return 'info'; }
    if (status === 'refunded' || status === 'authorization_expired' || status === 'failed_to_gateway') { return 'neutral'; }
    if (status === 'authorized' || status === 'submitted_for_settlement' || status === 'void') { return 'warn'; }
    return 'bad';
  }

  computeReturnsSectionTitle(): string {
    const returns = this.returns_include?.data;
    if (!Array.isArray(returns) || returns.length === 0) {
      return 'Rejection details';
    }
    const allNoc = returns.every(item => String(item?.return_code || '').toUpperCase().startsWith('C'));
    return allNoc ? 'Notification of change details' : 'Rejection details';
  }

  transformKey(key: any): string {
    if (!key) { return ''; }
    return String(key).replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  private camalize(value: string): string {
    return this.transformKey(value?.replace(/_/g, ' '));
  }

  private money(cents: number | undefined | null): string {
    return cents ? ('$' + (cents / 100).toFixed(2) + ' USD') : '--';
  }

  private maskLast4(full: string | undefined): string {
    if (!full) { return '--'; }
    return `••••${String(full).slice(-4)}`;
  }

  get achAccountMasked(): string {
    const data = this.paymentObj?.bank_account?.data;
    return data?.last4 ? `••••${data.last4}` : this.maskLast4(data?.account_number);
  }

  get achRoutingMasked(): string {
    return this.maskLast4(this.paymentObj?.bank_account?.data?.routing_number);
  }

  get detailLeftRows(): DetailRow[] {
    const p = this.paymentObj;
    const rows: DetailRow[] = [
      {label: 'Amount', value: this.money(p.amount)},
      {label: 'Type', value: p.type ? this.camalize(p.type) : '--'},
      {label: 'Charge ID', value: p.id || '--'},
      {label: 'Status', value: p.status ? this.camalize(p.status) : '--'},
      {label: 'Authorization ID', value: p['authorization_id'] || '--'},
      {label: 'Statement Descriptor', value: p['statement_description'] || '--'},
      {label: 'Transaction Date', value: this.datePipe.transform(p.created_at, 'MM/dd/yyyy hh:mm a') || '--'},
    ];
    if (p.amount_refunded > 0) {
      rows.push({label: 'Amount Refunded', value: this.money(p.amount_refunded)});
    }
    if (p.captured === 1 && p.amount_captured !== p.amount) {
      rows.push({label: 'Amount Captured', value: this.money(p.amount_captured)});
    }
    return rows;
  }

  get detailRightRows(): DetailRow[] {
    const p = this.paymentObj;
    return [
      {label: 'Created By', value: p['created_by'] || '--'},
      {label: 'Created At', value: this.datePipe.transform(p.created_at, 'MM/dd/yyyy hh:mm') || '--'},
      {label: 'Updated At', value: this.datePipe.transform(p.updated_at, 'MM/dd/yyyy hh:mm') || '--'},
      {label: 'Response Message', value: p['response_message'] || '--'},
    ];
  }

  get bankAccountRows(): DetailRow[] {
    const b = this.paymentObj.bank_account?.data;
    return [
      {label: 'Account Type', value: b?.account_type ? this.camalize(b.account_type) : '--'},
      {label: 'Account Number', value: this.achAccountMasked},
      {label: 'Routing Number', value: this.achRoutingMasked},
      {label: 'Account Holder Name', value: [b?.first_name, b?.last_name].filter(Boolean).join(' ') || '--'},
      {label: 'Business Name', value: b?.company_name || '--'},
      {label: 'Sec Code', value: this.paymentObj.sec_code || '--'},
    ];
  }

  get returnsRows(): DetailRow[] {
    const items = this.returns_include?.data || [];
    const rows: DetailRow[] = [];
    for (const item of items) {
      for (const [key, value] of Object.entries(item)) {
        if (key === 'object') { continue; }
        rows.push({label: this.transformKey(key), value});
      }
    }
    return rows;
  }

  get transactionMetadataRows(): DetailRow[] {
    return (this.transaction_metadata?.data || []).map(item => ({label: item.key, value: item.value}));
  }

  redirectToCustomer(id) {
    this.routerWithIdNewTab(this.redirect_link_customer, id);
  }

  updateDescription() {
    const postBody = {charge_description: this.paymentObj.charge_description ? this.paymentObj.charge_description.trim() : null};
    this._updatePaymentDescription(postBody);
  }

  onRefund(): void {
    const isPartial = this.paymentObj.status === 'partially_settled' || this.paymentObj.status === 'partial_refund';
    const data = {
      id: this.paymentObj.id,
      amount_captured: (this.paymentObj.amount_captured - this.paymentObj.amount_refunded),
      amount: ((isPartial ? this.paymentObj.amount_approved : this.paymentObj.amount) - this.paymentObj.amount_refunded),
      isAchRefund: true
    };
    localStorage.setItem(this.CUSTOMER_DATA, JSON.stringify(data));
    this.router.navigate(['../create-refund'], {queryParams: {id: data['id']}, relativeTo: this.route});
  }

  retryTransaction() {
    const transactionId = this.paymentObj.id;
    this.postRetryTransactionUrl = AppConstants.BASE_URL + AppConstants.SERVICE_NAME_ACH_PAYLIANCE_DEBIT_CREDIT + '/' + transactionId + '/retry';
    this.achChargePost({});
  }

  viewAchPDF(transaction_id: string) {
    this._getTransactionPdf = AppConstants.BASE_URL + '/get' + '/' + transaction_id + '/ach-transaction';
    this.getTransactionPDF();
    this.showSnackBar('File is loading.', 'Ok', 3000);
  }

  protected passPaymentDetails(paymentDetails) {
    this.payment2 = paymentDetails;
    this.paymentObj = paymentDetails.data;
    this.transaction_metadata = paymentDetails.data.transaction_metadata;
    this.extra_metadata = paymentDetails.data.extra_metadata;
    this.returns_include = paymentDetails.data.returns || {data: []};
    this.returnsSectionTitle = this.computeReturnsSectionTitle();
    this.loadFlag = false;
  }

  protected passApiError(error) {
    this.loadFlag = false;
    this.transaction_metadata = [];
    this.returns_include = {data: []};
  }

  protected passUpdateResponse(res) {
    this.loadFlag = true;
    this.getPaymentDetails();
  }

  protected passUpdateError(error) {
    this.loadFlag = false;
    this.errorMsg = error.message;
    this.showSnackBar('Error in update', 'Ok', 3000);
  }

  protected passRetryTransactionResponse(res) {
    this.loadFlag = false;
    this.showSnackBar('Transaction has been retried.', 'Ok', 3000);
  }

  protected passRetryError(error) {
    this.loadFlag = false;
    this.errorMsg = error.message;
    this.showSnackBar('Error in retry', 'Ok', 3000);
  }

  protected passUpdatePaymentDescriptionResponse(res) {
    this.topErrorMessage = null;
    this.errorMsg = null;
    this.editDescriptionFlag = false;
    this.paymentObj = res.data;
  }

  protected passUpdatePaymentDescriptionError(error) {
    this.editDescriptionFlag = true;
    this.errorMsg = error.message;
  }

  protected passTransactionPdfResponse(res: any) {
    this.fileURL = URL.createObjectURL(res);
  }

  protected passTransactionPdfError(err: any) {
  }

  protected passTransactionPdfComplete() {
    const link = document.createElement('a');
    link.href = this.fileURL;
    const b = this.paymentObj.bank_account?.data;
    const holder = b ? `${b.first_name || ''}${b.last_name || ''}`.replace(/\s/g, '').toUpperCase() : '';
    const createdDate = this.datePipe.transform(Number(this.paymentObj?.created_at) * 1000, 'dd_MM_yyyy');
    link.download = `${holder}_${createdDate}_${this.paymentObj?.id || ''}.pdf`;
    link.click();
  }
}
